import {
  CobraEmployeesListType,
  CurrentEmployeesType,
  CurrentInsuranceType,
  SingleDependentType,
} from "types";
import { IEmployeeCensusFormRow } from "uicomponents/EmployeeTable/EmployeeTable";
import {
  convertBoolIntoString,
  convertToTitleCase,
  ConvertToValidDate,
} from "utils/helper";
import {
  AddNewDependentFormType,
  CurrentInsuranceFormType,
  EditDependentFormType,
} from "./formTypes";
import { validRenewalDate } from "./helper";

const DEFAULT_EMPLOYEE_OBJECT = {
  firstName: "",
  lastName: "",
  participantType: "",
  gender: "",
  dateOfBirth: "",
  zip: "",
  currentPlanName: "",
  currentEnrollTier: "",
  currentMonthlyPremium: "",
  monthlyPremiumPostRenewal: "",
  uuid: "",
};

export const CurrentCoverageFormValues = (
  formData: CurrentEmployeesType
): IEmployeeCensusFormRow => {
  const employeeCensusesinfo = formData?.employeeCensuses;

  const filterHealthAppEmpCensus = employeeCensusesinfo?.filter(
    (emp) => emp?.isCompanyEmployee && !emp?.email
  );

  if (filterHealthAppEmpCensus?.length === 0) {
    const emptyEmployees = Array(5).fill(DEFAULT_EMPLOYEE_OBJECT);

    return {
      employees: emptyEmployees,
    };
  }

  const currentEmpMap: any = [];

  filterHealthAppEmpCensus?.forEach((eachEmp) => {
    currentEmpMap.push({
      uuid: eachEmp.uuid,
      firstName: eachEmp?.firstName || "",
      lastName: eachEmp?.lastName || "",
      participantType: convertToTitleCase("EMPLOYEE"),
      gender: convertToTitleCase(eachEmp?.gender) || "",
      dateOfBirth: ConvertToValidDate(eachEmp?.dateOfBirth) || "",
      zip: eachEmp?.homeZip || "",
      currentPlanName: eachEmp.healthInsInfo?.currentPlanName || "",
      currentEnrollTier:
        convertToTitleCase(eachEmp.healthInsInfo?.currentEnrollmentTier) || "",
      currentMonthlyPremium:
        eachEmp.healthInsInfo?.currentMonthlyPremium?.toString() || "",
      monthlyPremiumPostRenewal:
        eachEmp.healthInsInfo?.monthlyPremiumPostRenewal?.toString() || "",
    });

    if (eachEmp.dependentCensuses) {
      eachEmp.dependentCensuses.forEach((eachDependent) => {
        currentEmpMap.push({
          uuid: eachDependent?.uuid,
          firstName: eachDependent?.firstName || "",
          lastName: eachDependent?.lastName || "",
          participantType: convertToTitleCase(eachDependent?.participantType),
          gender: convertToTitleCase(eachDependent?.gender) || "",
          dateOfBirth: ConvertToValidDate(eachDependent?.dateOfBirth) || "",
          zip: eachDependent?.homeZip || "",
          currentPlanName: eachEmp?.healthInsInfo?.currentPlanName || "",
          currentEnrollTier:
            convertToTitleCase(eachEmp?.healthInsInfo?.currentEnrollmentTier) ||
            "",
          currentMonthlyPremium:
            eachEmp?.healthInsInfo?.currentMonthlyPremium?.toString() || "",
          monthlyPremiumPostRenewal:
            eachEmp?.healthInsInfo?.monthlyPremiumPostRenewal?.toString() || "",
        });
      });
    }
  });

  return {
    employees: currentEmpMap,
  };
};

export const cobraCensusInitialValue = (
  cobraCensusesInfo: CobraEmployeesListType
): IEmployeeCensusFormRow => {
  //TODO: once we have BE set up: check for Emp and depandents only (no owners)
  if (cobraCensusesInfo?.length === 0) {
    const emptyEmployees = Array(5).fill(DEFAULT_EMPLOYEE_OBJECT);

    return {
      employees: emptyEmployees,
    };
  }

  const cobraEmployees: any = [];

  cobraCensusesInfo
    ?.filter((employee) => employee.participantType === "EMPLOYEE")
    ?.forEach((eachEmp) => {
      cobraEmployees.push({
        uuid: eachEmp.uuid,
        firstName: eachEmp?.firstName || "",
        lastName: eachEmp?.lastName || "",
        participantType: convertToTitleCase("EMPLOYEE"),
        gender: convertToTitleCase(eachEmp?.gender) || "",
        dateOfBirth: ConvertToValidDate(eachEmp?.dateOfBirth) || "",
        zip: eachEmp?.homeZip || "",
        currentPlanName: eachEmp.healthInsInfo?.currentPlanName || "",
        currentEnrollTier:
          convertToTitleCase(eachEmp.healthInsInfo?.currentEnrollmentTier) ||
          "",
        currentMonthlyPremium:
          eachEmp.healthInsInfo?.currentMonthlyPremium?.toString() || "",
        monthlyPremiumPostRenewal:
          eachEmp.healthInsInfo?.monthlyPremiumPostRenewal?.toString() || "",
      });

      if (eachEmp.cobraDependents) {
        eachEmp.cobraDependents.forEach((eachDependent) => {
          cobraEmployees.push({
            uuid: eachDependent?.uuid,
            firstName: eachDependent?.firstName || "",
            lastName: eachDependent?.lastName || "",
            participantType: convertToTitleCase(eachDependent?.participantType),
            gender: convertToTitleCase(eachDependent?.gender) || "",
            dateOfBirth: ConvertToValidDate(eachDependent?.dateOfBirth) || "",
            zip: eachDependent?.homeZip || "",
            currentPlanName:
              eachDependent?.healthInsInfo?.currentPlanName || "",
            currentEnrollTier:
              convertToTitleCase(
                eachDependent?.healthInsInfo?.currentEnrollmentTier
              ) || "",
            currentMonthlyPremium:
              eachDependent?.healthInsInfo?.currentMonthlyPremium?.toString() ||
              "",
            monthlyPremiumPostRenewal:
              eachDependent?.healthInsInfo?.monthlyPremiumPostRenewal?.toString() ||
              "",
          });
        });
      }
    });

  return {
    employees: cobraEmployees,
  };
};

export const currentInsuranceFormValues = (
  formData: CurrentInsuranceType
): CurrentInsuranceFormType => {
  const healthInfo = formData?.healthInsuranceInformation;
  const documentList = formData?.healthInsuranceApplication?.[0].documents;
  return {
    yearFounded: formData?.yearFounded?.toString() || "",
    sicCode: formData?.sicCode || "",
    controlledGroup: convertBoolIntoString(healthInfo?.controlledGroup),
    startingCoverageDate: ConvertToValidDate(healthInfo?.startingCoverageDate),
    healthInsuranceInvoice:
      documentList?.find(
        (item) => item.applicationField === "healthInsuranceInvoice"
      )?.uuid || "",
    sbcDocument:
      documentList?.find((item) => item.applicationField === "sbcDocument")
        ?.uuid || "",
    renewalRateDocument:
      documentList?.find(
        (item) => item.applicationField === "renewalRateDocument"
      )?.uuid || "",
    isOnPeo: convertBoolIntoString(healthInfo?.isOnPeo),
    currentPeo: healthInfo?.currentPeo || "",
    currentPeoOther: healthInfo?.currentPeoOther || "",
    currentlyOfferHealthInsurance: convertBoolIntoString(
      formData?.healthInsuranceInformation?.currentlyOfferHealthInsurance
    ),
    currentRenewalDate: formData?.healthInsuranceInformation
      ?.currentlyOfferHealthInsurance
      ? !validRenewalDate(
          formData?.healthInsuranceInformation?.currentRenewalDate
        )
      : false,
  };
};

export const addNewDependentInitialFormValues = (): AddNewDependentFormType => {
  return {
    firstName: "",
    lastName: "",
    homeZip: "",
    gender: "",
    participantType: "",
    dateOfBirth: "",
  };
};

export const editDependentInitialFormValues = (
  formData: SingleDependentType
): EditDependentFormType => {
  return {
    firstName: formData.firstName || "",
    lastName: formData.lastName || "",
    homeZip: formData.homeZip || "",
    gender: convertToTitleCase(formData?.gender) || "",
    participantType: convertToTitleCase(formData?.participantType),
    dateOfBirth: ConvertToValidDate(formData?.dateOfBirth) || "",
  };
};
